import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { TenantService } from './tenant.service';

/**
 * v1.6.679 — Announcements service migrated to /v1/tenant/announcements.
 *
 * v1.6.666 hit /v1/announcements (the super-admin path) on the assumption
 * that tenant Bearer tokens had read access there. The backend has since
 * introduced a proper tenant-namespace endpoint per the new spec — same
 * shape but server-side filters drafts/archived for this audience, so
 * the client no longer needs to send `status=published`.
 *
 * Resolves the v1.6.666 standing backend dep: "verify /v1/announcements
 * works for tenant Bearer tokens." Answer: it doesn't, but there's now
 * a dedicated tenant endpoint that does.
 *
 * New in this revision:
 *   - GET /v1/tenant/announcements        — list (was /v1/announcements)
 *   - GET /v1/tenant/announcements/{id}   — show by id (NEW)
 *   - title=<partial>                     — new server-side title filter
 *   - status=published implicit           — no longer sent client-side
 *
 * Image shape adds an explicit `size` (bytes) field; status is narrowed
 * to the `'published'` literal for fresh responses but the type retains
 * the full union for defensiveness against older cached data.
 */
export interface Announcement {
  id: number;
  title: string;
  description: string;
  status: 'draft' | 'published' | 'archived';
  published_at: string | null;
  image: {
    id: number;
    url: string | null;
    size?: number;
    thumbnail_url: string | null;
    medium_url: string | null;
    large_url: string | null;
    name: string;
    original_name: string;
    mime_type: string | null;
    created_at?: string | null;
  } | null;
  // Kept on the interface for backwards-compat with any rendered surfaces
  // that still reference these — the tenant-facing endpoint doesn't
  // include them per the new spec, so they may arrive undefined.
  created_by?: { id: number; name: string } | null;
  updated_by?: { id: number; name: string } | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface AnnouncementsListResponse {
  announcements: Announcement[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  } | null;
}

@Injectable({ providedIn: 'root' })
export class AnnouncementsService {
  /** v1.6.679 — Moved to the tenant namespace. */
  private get base(): string { return `${this.tenant.apiBaseUrl}/v1/tenant/announcements`; }

  constructor(private http: HttpClient, private tenant: TenantService) {}

  /**
   * GET /v1/tenant/announcements
   *
   * Backend filters drafts/archived out server-side for this audience,
   * so the client passes only pagination + optional title search.
   * per_page defaults to 20 — a reasonable inbox-sized list for a
   * header panel; max 100 per spec.
   *
   * Parser handles the wrapped `data: { announcements, meta }` shape
   * (the new spec) and falls back tolerantly to a bare array in `data`
   * in case the tenant endpoint mirrors other tenant endpoints that
   * return `{ data: [...] }` directly.
   */
  listPublished(params?: { page?: number; per_page?: number; title?: string }): Observable<AnnouncementsListResponse> {
    let httpParams = new HttpParams();
    if (params?.page)              httpParams = httpParams.set('page', params.page);
    if (params?.per_page)          httpParams = httpParams.set('per_page', params.per_page);
    if (params?.title?.trim())     httpParams = httpParams.set('title', params.title.trim());
    return this.http.get<any>(this.base, { params: httpParams }).pipe(
      map(res => {
        const d = res?.data ?? {};
        const list: Announcement[] = Array.isArray(d?.announcements)
          ? d.announcements
          : Array.isArray(d) ? d : Array.isArray(res?.data) ? res.data : [];
        return {
          announcements: list,
          meta: d?.meta ?? res?.meta ?? null,
        };
      })
    );
  }

  /**
   * v1.6.679 — GET /v1/tenant/announcements/{id}
   *
   * Returns 404 for draft or archived announcements (spec behavior:
   * the tenant endpoint never reveals editorial state to operators).
   * Consumers should treat 404 the same as "not available" and not
   * surface a scary error — the announcement may have been archived
   * after being read in a previous session.
   */
  show(id: number): Observable<Announcement | null> {
    return this.http.get<any>(`${this.base}/${id}`).pipe(
      map(res => (res?.data ?? null) as Announcement | null),
    );
  }
}
